import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonRouterOutlet, ModalController, NavController, Platform } from '@ionic/angular';
import { CheckoutLeaveGuard } from './checkout-leave.guard';
import { checkoutRecoveryEnabled } from './checkout-recovery-lever';
import { Store } from '@ngrx/store';
import { AppState, AuthService, EventService, MyPenmateService } from '../services';
import { merge, Subscription } from 'rxjs';
import { take, takeWhile } from 'rxjs/operators';
import { LetterService } from '../services';
import { LoadingModal } from '../loading-modal/loading-modal';
import { MessageDeliveryModal } from '../message-delivery-modal/message-delivery-modal';
import { MessageTrackingModal } from '../message-tracking-modal/message-tracking-modal';
import { MessageIncompleteModal } from '../message-incomplete-modal/message-incomplete-modal';
import { UserActions, MyPenmateActions } from '../actions';
import { LegalTermsModal } from '../legal-terms-modal/legal-terms-modal';
import ErrorHandler from '../../lib/error-handler';

type PmReactNavigateIntent =
  | { to: 'add-credits'; penmateId: string; letterId?: string; returnTo: string }
  | { to: 'tracking'; penmateId: string; messageId: string; returnTo: string }
  | { to: 'needs-info'; penmateId: string; messageId: string; returnTo: string }
  | { to: 'legacy-message'; penmateId: string; messageId: string; emessage?: boolean; returnTo: string };

type PmReactEvent =
  | { type: 'thread.opened'; penmateId: string; messageType?: string; letterId?: string }
  | { type: 'thread.updated'; penmateId: string }
  | { type: 'thread.sent'; penmateId: string; text: string };

type PmReactMessengerProps = {
  penmateId?: string;
  penmate?: any;
  messageType?: string;
  letterId?: string;
  refreshKey?: number;
  checkoutDisabled?: boolean;
  sessionId?: number;
  onNavigate?: (intent: PmReactNavigateIntent) => void;
  onEvent?: (event: PmReactEvent) => void;
};

type PmReactMessagingApi = {
  mount: (el: Element, props: PmReactMessengerProps) => void;
  update: (el: Element, props: PmReactMessengerProps) => void;
  unmount: (el: Element) => void;
};

const JS_URL = '/assets/www/react/messaging/pmreact-messaging.js';
const SKELETON_JS_URL = '/assets/www/react/messaging/skeleton-loader.js';

const getSkeletonPrefetchUrl = (): string | null => {
  if (typeof document === 'undefined') return null;
  const link = document.querySelector(
    'link[as="script"][href*="skeleton-loader.js"][rel="preload"], link[as="script"][href*="skeleton-loader.js"][rel="prefetch"]',
  ) as HTMLLinkElement | null;
  return link && typeof link.href === 'string' && link.href ? link.href : null;
};

const getMessagingPrefetchUrl = (): string | null => {
  if (typeof document === 'undefined') return null;
  const link = document.querySelector(
    'link[as="script"][href*="pmreact-messaging.js"][rel="preload"], link[as="script"][href*="pmreact-messaging.js"][rel="prefetch"]',
  ) as HTMLLinkElement | null;
  return link && typeof link.href === 'string' && link.href ? link.href : null;
};

const getMessagingBundleUrl = (): string => {
  const w = window as any;
  const env = w && w.Penmate && w.Penmate.env ? w.Penmate.env : {};
  const envName = String(env.name || env.environment || '');
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isDev =
    /development/i.test(envName) || /(^localhost$|^127\.0\.0\.1$|ngrok\.io$)/i.test(host);
  if (!isDev) return getMessagingPrefetchUrl() || JS_URL;
  // In development we want a fresh bundle after rebuilds; stable filenames + SPA navigation
  // otherwise make it easy to end up with a stale cached JS artifact.
  const sep = JS_URL.includes('?') ? '&' : '?';
  return `${JS_URL}${sep}v=${Date.now()}`;
};

const getMessagingFallbackUrl = (primaryUrl: string): string | null => {
  if (!primaryUrl) return null;
  try {
    const primary = new URL(primaryUrl, document.baseURI);
    const fallback = new URL(JS_URL, document.baseURI);
    if (primary.origin === fallback.origin && primary.pathname === fallback.pathname) {
      return null;
    }
  } catch {
    if (primaryUrl === JS_URL) return null;
  }
  return JS_URL;
};

type ScriptLoadVerify = () => boolean;

type ScriptLoadErrorRecord = {
  message?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  reason?: string;
};

type ScriptDiagnostics = {
  url: string;
  script?: {
    src?: string;
    loaded?: boolean;
    failed?: boolean;
    async?: boolean;
    crossOrigin?: string | null;
  } | null;
  scripts?: string[];
  performance?: {
    transferSize?: number;
    encodedBodySize?: number;
    decodedBodySize?: number;
    duration?: number;
  } | null;
  fetch?: {
    status?: number;
    contentType?: string | null;
    contentLength?: string | null;
    contentEncoding?: string | null;
    etag?: string | null;
    cacheControl?: string | null;
    cfCacheStatus?: string | null;
    age?: string | null;
    via?: string | null;
    xCache?: string | null;
    server?: string | null;
    snippetStart?: string | null;
    snippetEnd?: string | null;
    error?: string;
  } | null;
  errors?: ScriptLoadErrorRecord[];
  pmReactPresent?: boolean;
};

const getScriptState = (id: string): ScriptDiagnostics['script'] => {
  if (typeof document === 'undefined') return null;
  const script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) return null;
  return {
    src: script.src,
    loaded: (script as any).__pmLoaded,
    failed: (script as any).__pmFailed,
    async: script.async,
    crossOrigin: script.crossOrigin,
  };
};

const getPerformanceEntry = (url: string): ScriptDiagnostics['performance'] | null => {
  if (typeof performance === 'undefined' || !url) return null;
  const entries = performance.getEntriesByName(url);
  if (!entries || !entries.length) return null;
  const entry = entries[entries.length - 1] as PerformanceResourceTiming;
  return {
    transferSize: entry.transferSize,
    encodedBodySize: entry.encodedBodySize,
    decodedBodySize: entry.decodedBodySize,
    duration: entry.duration,
  };
};

const collectBundleDiagnostics = async (url: string, id: string): Promise<ScriptDiagnostics> => {
  const diagnostics: ScriptDiagnostics = {
    url,
    script: getScriptState(id),
    scripts:
      typeof document === 'undefined'
        ? []
        : Array.from(document.querySelectorAll('script[src*="pmreact-messaging"]')).map((el) =>
            (el as HTMLScriptElement).src,
          ),
    performance: getPerformanceEntry(url),
    fetch: null,
    errors: [],
    pmReactPresent:
      typeof window !== 'undefined' && !!((window as any).PmReact && (window as any).PmReact.messaging),
  };
  if (!url || typeof fetch === 'undefined') return diagnostics;
  try {
    const response = await fetch(url, {
      cache: 'no-store',
      mode: 'cors',
      headers: {
        Range: 'bytes=0-1023',
      },
    });
    const text = await response.text();
    diagnostics.fetch = {
      status: response.status,
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length'),
      contentEncoding: response.headers.get('content-encoding'),
      etag: response.headers.get('etag'),
      cacheControl: response.headers.get('cache-control'),
      cfCacheStatus: response.headers.get('cf-cache-status'),
      age: response.headers.get('age'),
      via: response.headers.get('via'),
      xCache: response.headers.get('x-cache'),
      server: response.headers.get('server'),
      snippetStart: text.slice(0, 200),
      snippetEnd: text.length > 200 ? text.slice(-200) : null,
    };
  } catch (err) {
    diagnostics.fetch = {
      error: err instanceof Error ? err.message : String(err),
    };
  }
  return diagnostics;
};

const captureScriptErrors = (bundleUrl: string): { errors: ScriptLoadErrorRecord[]; stop: () => void } => {
  const errors: ScriptLoadErrorRecord[] = [];
  if (typeof window === 'undefined') return { errors, stop: () => undefined };
  const matchesBundle = (value?: string | null): boolean =>
    typeof value === 'string' && value.length ? value.includes('pmreact-messaging') || value.includes(bundleUrl) : false;

  const onError = (event: ErrorEvent) => {
    if (!event) return;
    const filename = event.filename || (event.error && (event.error as any).fileName);
    const stack = event.error instanceof Error ? event.error.stack : undefined;
    if (!matchesBundle(filename) && !matchesBundle(stack)) return;
    errors.push({
      message: event.message,
      filename,
      lineno: event.lineno,
      colno: event.colno,
      stack,
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = (event && (event as any).reason) || null;
    const message = reason instanceof Error ? reason.message : reason ? String(reason) : undefined;
    const stack = reason instanceof Error ? reason.stack : undefined;
    if (!matchesBundle(message) && !matchesBundle(stack)) return;
    errors.push({
      message,
      reason: message,
      stack,
    });
  };

  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onRejection);

  return {
    errors,
    stop: () => {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onRejection);
    },
  };
};

const markScriptFailed = (id: string): void => {
  if (typeof document === 'undefined') return;
  const script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) return;
  (script as any).__pmFailed = true;
  (script as any).__pmLoaded = false;
  script.remove();
};

const getMessengerScriptSnapshot = (id: string): Record<string, unknown> => {
  if (typeof window === 'undefined') return { unavailable: true };
  const w = window as any;
  const pmReact = w.PmReact;
  const pmReactKeys =
    pmReact && typeof pmReact === 'object' ? Object.keys(pmReact).slice(0, 20) : null;
  let scriptSnapshot: Record<string, unknown> | null = null;
  if (typeof document !== 'undefined') {
    const script = document.getElementById(id) as HTMLScriptElement | null;
    if (script) {
      scriptSnapshot = {
        src: script.src || null,
        async: script.async,
        defer: script.defer,
        type: script.type || null,
        crossOrigin: script.crossOrigin || null,
        readyState: (script as any).readyState || null,
        pmLoaded: (script as any).__pmLoaded || false,
        pmFailed: (script as any).__pmFailed || false,
      };
    }
  }
  return {
    hasPmReact: !!pmReact,
    pmReactType: typeof pmReact,
    hasMessaging: !!(pmReact && pmReact.messaging),
    pmReactKeys,
    script: scriptSnapshot,
  };
};

const addCacheBust = (url: string): string => {
  try {
    const parsed = new URL(url, document.baseURI);
    parsed.searchParams.set('pmcb', String(Date.now()));
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}pmcb=${Date.now()}`;
  }
};

const loadScriptOnce = (
  src: string,
  id: string,
  opts?: { verifyGlobal?: ScriptLoadVerify },
): Promise<void> => {
  const verifyGlobal = opts && opts.verifyGlobal;
  const hasGlobal = () => (typeof verifyGlobal === 'function' ? verifyGlobal() : true);
  const existingById = document.getElementById(id) as HTMLScriptElement | null;
  if (existingById) {
    if ((existingById as any).__pmFailed) {
      existingById.remove();
    } else {
      const desiredSrc = new URL(src, document.baseURI).toString();
      const existingSrc = existingById.src ? new URL(existingById.src, document.baseURI).toString() : '';

      if (existingSrc && existingSrc !== desiredSrc) {
        existingById.remove();
      } else if ((existingById as any).__pmLoaded && !hasGlobal()) {
        (existingById as any).__pmFailed = true;
        existingById.remove();
      } else {
        return new Promise((resolve, reject) => {
          const w = window as any;
          if ((existingById as any).__pmLoaded) return resolve();
          if (w && w.PmReact && w.PmReact.messaging) return resolve();
          if ((existingById as any).__pmFailed) {
            return reject(new Error(`Failed to load ${src}`));
          }
          existingById.addEventListener(
            'load',
            () => {
              if (!hasGlobal()) {
                (existingById as any).__pmFailed = true;
                return reject(new Error('PmReact.messaging is not available after bundle load'));
              }
              resolve();
            },
            { once: true },
          );
          existingById.addEventListener(
            'error',
            () => reject(new Error(`Failed to load ${src}`)),
            { once: true },
          );
        });
      }
    }
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.addEventListener('load', () => {
      (script as any).__pmLoaded = true;
      if (!hasGlobal()) {
        (script as any).__pmFailed = true;
        reject(new Error('PmReact.messaging is not available after bundle load'));
        return;
      }
      resolve();
    });
    script.addEventListener('error', () => {
      (script as any).__pmFailed = true;
      reject(new Error(`Failed to load ${src}`));
    });
    document.body.appendChild(script);
  });
};

@Component({
  selector: 'pm-page-react-messenger',
  templateUrl: 'react-messenger.html',
  styleUrls: ['./react-messenger.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReactMessengerPage implements AfterViewInit, OnDestroy {
  @ViewChild('host', { static: true }) hostRef: ElementRef;

  private isMounted = false;
  private isAlive = true;
  private threadRefreshKey = 0;
  private bootStarted = false;
  private routeSyncSub: Subscription | null = null;
  private userDeviceInfoSub: Subscription | null = null;
  private userSub: Subscription | null = null;
  private penmateListRequested = false;
  private penmateListSub: Subscription | null = null;
  penmate: any = {};
  checkoutDisabled = false;
  user: any = null;

  idVerificationBannerDismissed = false;
  private idVerificationBannerPenmateId: string | null = null;

  private readonly bodyClass = 'pm-react-messenger--active';
  isBooting = true;
  bootError: string | null = null;

  constructor(
    private zone: NgZone,
    private route: ActivatedRoute,
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private store: Store<AppState>,
    private letterService: LetterService,
    private myPenmateService: MyPenmateService,
    private authService: AuthService,
    private eventService: EventService,
    private userActions: UserActions,
    private myPenmateActions: MyPenmateActions,
    private checkoutLeaveGuard: CheckoutLeaveGuard,
    private platform: Platform,
    @Optional() private routerOutlet: IonRouterOutlet,
  ) {}

  // Checkout recovery: while the bundle reports an active checkout, (1) the
  // hardware back button is routed through the leave guard like the header
  // back (Ionic's default outlet pop is an imperative navigation the guard
  // cannot otherwise tell from the page's own redirects), and (2) the
  // outlet's swipe-back gesture is off — it too is an imperative pop, and
  // its transition has already played by the time a guard could refuse it.
  // Outside checkout both are restored and Ionic's defaults run untouched.
  private checkoutBackButtonSub: Subscription | null = null;
  private swipeGestureSuspended = false;
  private onCheckoutActiveChanged = (evt: Event) => {
    const detail = (evt as CustomEvent).detail || {};
    this.setCheckoutLeaveInterception(!!detail.draftId);
  };
  private setCheckoutLeaveInterception(active: boolean): void {
    // Kill lever off: never intercept, and tear down any interception that
    // was armed before the lever flipped.
    active = active && checkoutRecoveryEnabled();
    if (active && !this.checkoutBackButtonSub) {
      this.checkoutBackButtonSub = this.platform.backButton.subscribeWithPriority(1, () => {
        this.zone.run(() => {
          this.checkoutLeaveGuard.markHeaderBack();
          // Same pop Ionic's outlet performs for the default handler.
          this.navCtrl.pop().catch(() => undefined);
        });
      });
    } else if (!active && this.checkoutBackButtonSub) {
      this.checkoutBackButtonSub.unsubscribe();
      this.checkoutBackButtonSub = null;
    }
    // `swipeGesture` is write-only on the outlet; the app runs mode: 'ios'
    // with the gesture on, so "restore" means back on.
    if (!this.routerOutlet) return;
    if (active && !this.swipeGestureSuspended) {
      this.swipeGestureSuspended = true;
      this.routerOutlet.swipeGesture = false;
    } else if (!active && this.swipeGestureSuspended) {
      this.swipeGestureSuspended = false;
      this.routerOutlet.swipeGesture = true;
    }
  }

  // Ionic keeps a pushed-past page alive in its stack; only the visible
  // messenger listens and intercepts.
  ionViewWillEnter(): void {
    window.addEventListener('pmreact:checkout_active_changed', this.onCheckoutActiveChanged);
    this.setCheckoutLeaveInterception(!!(window as any).__pmreact_checkout_draft_id && this.isMounted);
  }

  ionViewDidLeave(): void {
    window.removeEventListener('pmreact:checkout_active_changed', this.onCheckoutActiveChanged);
    this.setCheckoutLeaveInterception(false);
  }

  private showLegalTermsModal(): Promise<boolean> {
    return new Promise((resolve) => {
      this.zone.run(() => {
        this.modalCtrl
          .create({
            component: LegalTermsModal,
            componentProps: {},
          })
          .then(async (modal) => {
            await modal.present();
            await modal.onDidDismiss();
            this.store
              .select((state) => (state as any).user && (state as any).user.tosAgreement)
              .pipe(take(1))
              .subscribe((tosAgreement: any) => resolve(tosAgreement === true));
          })
          .catch(() => resolve(false));
      });
    });
  }

  private isDevelopment(): boolean {
    const w = window as any;
    return (
      typeof w !== 'undefined' &&
      w.Penmate &&
      w.Penmate.env &&
      w.Penmate.env.name === 'development'
    );
  }

  onGoBack = () => {
    this.checkoutLeaveGuard.markHeaderBack();
    this.navCtrl.navigateBack('/my-penmates');
  };

  private getIdVerificationRecipientName(): string {
    const u: any = this.user;
    const r = u && typeof u === 'object' ? u.id_verification_recipient : null;
    const name = r && typeof r === 'object' ? r.name : null;
    return typeof name === 'string' ? name : '';
  }

  private penmateMatchesRoute(routeId: string | null, penmate: any): boolean {
    if (!routeId) return true;
    if (!penmate || typeof penmate !== 'object') return false;
    const penmateId = penmate.id != null ? String(penmate.id) : '';
    if (/^\d+$/.test(routeId)) return penmateId === routeId;
    const slug = typeof penmate.slug === 'string' ? penmate.slug : '';
    if (slug && slug.toLowerCase() === routeId.toLowerCase()) return true;
    return penmateId === routeId;
  }

  private getResolvedPenmateId(routeId: string | null): string | null {
    if (routeId && /^\d+$/.test(routeId)) return routeId;
    if (!this.penmateMatchesRoute(routeId, this.penmate)) return null;
    const id = this.penmate && typeof this.penmate === 'object' ? (this.penmate as any).id : null;
    return id != null ? String(id) : null;
  }

  private findPenmateInList(routeId: string | null, penmates: any[]): any | null {
    if (!routeId || !Array.isArray(penmates) || penmates.length === 0) return null;
    const token = String(routeId).trim();
    if (!token) return null;
    if (/^\d+$/.test(token)) {
      const match = penmates.find((p) => p && String(p.id) === token);
      if (match) return match;
    }
    const lower = token.toLowerCase();
    return (
      penmates.find((p) => {
        if (!p || typeof p !== 'object') return false;
        const slug = typeof p.slug === 'string' ? p.slug : '';
        if (slug && slug.toLowerCase() === lower) return true;
        return String(p.id) === token;
      }) || null
    );
  }

  private applyPenmateMatch(penmate: any): void {
    if (!penmate) return;
    this.penmate = penmate;
    this.store.dispatch(this.myPenmateActions.viewPenmate(penmate));
    this.resetDismissedBannerIfNeeded();
    if (!this.isMounted) return;
    try {
      this.render();
    } catch {
      // ignore render races
    }
  }

  private resolvePenmateFromList(routeId: string): void {
    if (this.penmateListRequested) return;
    this.penmateListRequested = true;

    this.store
      .select((state) => state.myPenmates.penmates)
      .pipe(take(1))
      .subscribe((penmates) => {
        const match = this.findPenmateInList(routeId, Array.isArray(penmates) ? penmates : []);
        if (match) {
          this.applyPenmateMatch(match);
          return;
        }

        this.penmateListSub = this.myPenmateService.getPenmates().subscribe(
          (list) => {
            const nextMatch = this.findPenmateInList(routeId, Array.isArray(list) ? list : []);
            if (nextMatch) {
              this.applyPenmateMatch(nextMatch);
              return;
            }
            this.zone.run(() => {
              this.isBooting = false;
              this.bootError = null;
              this.navCtrl.navigateRoot('/my-penmates');
            });
          },
          () => {
            this.zone.run(() => {
              this.isBooting = false;
              this.bootError = 'Please try again.';
            });
          },
        );
      });
  }

  get idVerificationBannerText(): string {
    const name = this.getIdVerificationRecipientName();
    if (name) {
      return `Your recent message to ${name} requires additional verification to complete delivery.`;
    }
    return 'Your recent message requires additional verification to complete delivery.';
  }

  private getCurrentPenmateId(): string | null {
    const p: any = this.penmate;
    const id = p && typeof p === 'object' ? p.id : null;
    return id != null ? String(id) : null;
  }

  private shouldShowIdVerificationBanner(): boolean {
    if (this.idVerificationBannerDismissed) return false;
    const u: any = this.user;
    if (!u || typeof u !== 'object') return false;
    if (!u.id_verification_required) return false;
    if (!u.id_verification_pending_review) return false;
    if (u.id_verification_completed) return false;
    const recipient = u.id_verification_recipient;
    if (!recipient || typeof recipient !== 'object') return false;
    const recipientId = (recipient as any).id != null ? String((recipient as any).id) : '';
    const penmateId = this.getCurrentPenmateId();
    if (!recipientId || !penmateId) return false;
    return recipientId === penmateId;
  }

  get showIdVerificationBanner(): boolean {
    return this.shouldShowIdVerificationBanner();
  }

  onDismissIdVerificationBanner = (evt?: Event) => {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
    this.idVerificationBannerDismissed = true;
  };

  onGoToMyAccount = () => {
    this.navCtrl.navigateForward('/my-account');
  };

  private resetDismissedBannerIfNeeded(): void {
    const penmateId = this.getCurrentPenmateId();
    if (!penmateId) return;
    if (this.idVerificationBannerPenmateId === penmateId) return;
    this.idVerificationBannerPenmateId = penmateId;
    this.idVerificationBannerDismissed = false;
  }

  private getV1RedirectUrl(penmateId: string): string {
    const qp = this.route.snapshot.queryParams || {};
    const rawMessageType = typeof qp['messageType'] === 'string' ? String(qp['messageType']) : '';
    const messageType = rawMessageType.toLowerCase().trim();
    const letterIdRaw = qp['letterId'] || qp['messageId'] || qp['draftId'] || null;
    const letterId = letterIdRaw != null ? String(letterIdRaw) : '';

    const base = `/penmates/${encodeURIComponent(penmateId)}/v1`;
    if (letterId) return `${base}/messages/${encodeURIComponent(letterId)}`;
    if (messageType === 'postcard') return `${base}/new-postcard`;
    if (messageType === 'emessage' || messageType === 'letter') return `${base}/new-message`;
    return `${base}/messages`;
  }

  ngAfterViewInit(): void {
    if (this.bootStarted) return;
    this.bootStarted = true;
    window.addEventListener('pmreact:checkout_active_changed', this.onCheckoutActiveChanged);

    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.add(this.bodyClass);
    }

    this.isBooting = true;
    this.bootError = null;

    const penmateId = this.route.snapshot.paramMap.get('id');

    this.store
      .select((state) => state.myPenmates.currentPenmate)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((penmate) => {
        if (penmate && this.penmateMatchesRoute(penmateId, penmate)) {
          this.penmate = penmate;
        } else if (!penmate || penmateId) {
          this.penmate = {};
        }
        this.resetDismissedBannerIfNeeded();
        if (!this.isMounted) return;
        try {
          this.render();
        } catch {
          // ignore render races
        }
      });

    // Gate messenger behind an authenticated user (parity with other Angular routes).
    this.authService
      .authenticate()
      .pipe(take(1), takeWhile(() => this.isAlive))
      .subscribe(({ isAuthenticated, user }) => {
        if (!isAuthenticated || !user) {
          this.zone.run(() => {
            this.isBooting = false;
            this.bootError = null;
            this.navCtrl.navigateRoot('/login');
          });
          return;
        }

        this.user = user;
        // Keep Angular store consistent (legacy pages still read from it).
        this.store.dispatch(this.userActions.loginUser(user));

        if (!this.userSub) {
          this.userSub = this.store
            .select((state) => (state as any).user)
            .pipe(takeWhile(() => this.isAlive))
            .subscribe((nextUser: any) => {
              if (nextUser) this.user = nextUser;
              if (!this.isMounted) return;
              // Re-render so banner visibility/copy stays in sync with store updates.
              try {
                this.render();
              } catch {
                // ignore render races
              }
            });
        }

        // Mirror legacy `skipConfirmation`: derived from `state.user.deviceInfo.metrics === false`,
        // which is updated via DEVICE_INFO -> POST /metrics/device -> UPDATE_DEVICE_INFO_SUCCESS.
        if (!this.userDeviceInfoSub) {
          const redirectUrl = penmateId ? this.getV1RedirectUrl(penmateId) : null;
          this.userDeviceInfoSub = this.store
            .select((state) => (state as any).user && (state as any).user.deviceInfo)
            .pipe(takeWhile(() => this.isAlive))
            .subscribe((deviceInfo: any) => {
              const metrics = deviceInfo && typeof deviceInfo === 'object' ? deviceInfo.metrics : undefined;
              const nextDisabled = metrics === false;
              if (nextDisabled === this.checkoutDisabled) return;
              this.checkoutDisabled = nextDisabled;
              if (nextDisabled && redirectUrl) {
                this.zone.run(() => {
                  this.isBooting = false;
                  this.bootError = null;
                  this.navCtrl.navigateRoot(redirectUrl);
                });
                return;
              }
              if (!this.isMounted) return;
              try {
                this.render();
              } catch (e) {
                // ignore render races
              }
            });
        }

        if (penmateId) {
          this.resolvePenmateFromList(penmateId);
        }

        // Demo users should never see the new messenger UI; route them to legacy v1.
        this.store
          .select((state) => (state as any).user)
          .pipe(take(1), takeWhile(() => this.isAlive))
          .subscribe((stateUser: any) => {
            const deviceInfo =
              stateUser && typeof stateUser === 'object' ? (stateUser as any).deviceInfo : null;
            const metrics = deviceInfo && typeof deviceInfo === 'object' ? deviceInfo.metrics : undefined;
            const nextDisabled = metrics === false;
            this.checkoutDisabled = nextDisabled;
            if (nextDisabled && penmateId) {
              this.zone.run(() => {
                this.isBooting = false;
                this.bootError = null;
                this.navCtrl.navigateRoot(this.getV1RedirectUrl(penmateId));
              });
              return;
            }

            const tosAgreement = !!(stateUser && typeof stateUser === 'object' ? (stateUser as any).tosAgreement : null);
            const skipTos = metrics === false;

            if (!skipTos && !tosAgreement) {
              void (async () => {
                const accepted = await this.showLegalTermsModal();
                if (!accepted) {
                  this.zone.run(() => {
                    this.isBooting = false;
                    this.bootError = null;
                    this.navCtrl.navigateBack('/my-penmates');
                  });
                  return;
                }
                this.bootReactMessenger();
              })();
              return;
            }

            this.bootReactMessenger();
          });
      });
  }

  private async attemptBundleLoad(
    url: string,
    label: 'primary' | 'fallback',
    fallbackUrl: string | null,
  ): Promise<{ ok: boolean; error?: Error }> {
    const scriptId = 'pmreact-messaging-js';
    const errorCapture = captureScriptErrors(url);
    let loadError: Error | null = null;
    try {
      await loadScriptOnce(url, scriptId, {
        verifyGlobal: () => !!this.getApi(),
      });
    } catch (err) {
      loadError = err instanceof Error ? err : new Error(String(err));
    } finally {
      errorCapture.stop();
    }

    if (!loadError) return { ok: true };

    markScriptFailed(scriptId);
    const diagnostics = await collectBundleDiagnostics(url, scriptId);
    diagnostics.errors = errorCapture.errors;
    ErrorHandler.captureException(loadError, `React messenger bundle load failure (${label})`, {
      bundleUrl: url,
      fallbackUrl,
      prefetchUrl: getMessagingPrefetchUrl(),
      skeletonUrl: getSkeletonPrefetchUrl() || SKELETON_JS_URL,
      diagnostics,
    });
    return { ok: false, error: loadError };
  }

  private async loadMessengerBundle(bundleUrl: string, fallbackUrl: string | null): Promise<void> {
    const primary = await this.attemptBundleLoad(bundleUrl, 'primary', fallbackUrl);
    if (primary.ok) return;
    if (!fallbackUrl) throw primary.error;

    const fallbackAttemptUrl = addCacheBust(fallbackUrl);
    const fallback = await this.attemptBundleLoad(fallbackAttemptUrl, 'fallback', fallbackAttemptUrl);
    if (fallback.ok) {
      ErrorHandler.captureMessage('React messenger loaded via fallback', {
        bundleUrl,
        fallbackUrl: fallbackAttemptUrl,
        prefetchUrl: getMessagingPrefetchUrl(),
      });
      return;
    }
    throw fallback.error || primary.error;
  }

  private async bootReactMessenger(): Promise<void> {
    const bundleUrl = getMessagingBundleUrl();
    const fallbackUrl = getMessagingFallbackUrl(bundleUrl);
    // Load skeleton web component first (for immediate display), then React bundle
    try {
      await loadScriptOnce(getSkeletonPrefetchUrl() || SKELETON_JS_URL, 'pm-skeleton-loader-js').catch(() => {
        // Skeleton is non-critical; continue loading React even if it fails
      });
      await this.loadMessengerBundle(bundleUrl, fallbackUrl);
      this.render();
      this.startRouteSync();
      // Keep an Angular loader only until React mounts a first frame; then let React
      // render its own "loading" UI (skeletons) while data fetches.
      requestAnimationFrame(() => {
        this.zone.run(() => {
          this.isBooting = false;
        });
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      const error = err instanceof Error ? err : new Error(String(err));
      ErrorHandler.captureException(error, 'React messenger boot error', {
        bundleUrl,
        fallbackUrl,
        prefetchUrl: getMessagingPrefetchUrl(),
        skeletonUrl: getSkeletonPrefetchUrl() || SKELETON_JS_URL,
        scriptSnapshot: getMessengerScriptSnapshot('pmreact-messaging-js'),
      });
      this.zone.run(() => {
        this.isBooting = false;
        this.bootError = 'Please try again.';
      });
    }
  }

  ngOnDestroy(): void {
    this.isAlive = false;
    window.removeEventListener('pmreact:checkout_active_changed', this.onCheckoutActiveChanged);
    this.setCheckoutLeaveInterception(false);
    const el = (this.hostRef && this.hostRef.nativeElement) as Element | undefined;
    const api = this.getApi();
    if (el && api && typeof api.unmount === 'function') api.unmount(el);
    this.isMounted = false;
    if (this.routeSyncSub) {
      this.routeSyncSub.unsubscribe();
      this.routeSyncSub = null;
    }
    if (this.userDeviceInfoSub) {
      this.userDeviceInfoSub.unsubscribe();
      this.userDeviceInfoSub = null;
    }
    if (this.userSub) {
      this.userSub.unsubscribe();
      this.userSub = null;
    }
    if (this.penmateListSub) {
      this.penmateListSub.unsubscribe();
      this.penmateListSub = null;
    }

    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove(this.bodyClass);
    }
  }

  private getApi(): PmReactMessagingApi | null {
    const w = window as any;
    return (w && w.PmReact && w.PmReact.messaging) || null;
  }

  private render(): void {
    const el = (this.hostRef && this.hostRef.nativeElement) as Element | undefined;
    if (!el) return;

    const api = this.getApi();
    if (!api) throw new Error('PmReact.messaging is not available after bundle load');

    const routePenmateId = this.route.snapshot.paramMap.get('id');
    const penmateId = this.getResolvedPenmateId(routePenmateId) || undefined;
    const messageType = this.route.snapshot.queryParams['messageType'] || undefined;
    // Support redirect/resume flows that return with `messageId` (legacy param name).
    const letterId =
      this.route.snapshot.queryParams['letterId'] || this.route.snapshot.queryParams['messageId'] || undefined;

    const returnTo = window.location.pathname + window.location.search;

    const props: PmReactMessengerProps = {
      penmateId,
      penmate: this.penmate && (this.penmate as any).id ? this.penmate : undefined,
      messageType,
      letterId,
      checkoutDisabled: this.checkoutDisabled,
      refreshKey: this.threadRefreshKey,
      sessionId:
        this.eventService && (this.eventService as any) && (this.eventService as any).sessionId
          ? (this.eventService as any).sessionId
          : undefined,
      onEvent: (event) => {
        // Placeholder: later we’ll dispatch NgRx refresh actions here.
        if (this.isDevelopment()) {
          // eslint-disable-next-line no-console
          console.log('[react-messenger] event', event);
        }
      },
      onNavigate: (intent) => {
        this.zone.run(() => {
          if (this.isDevelopment()) {
            // eslint-disable-next-line no-console
            console.log('[react-messenger] navigate', intent);
          }
          if (!intent || !intent.to) return;
          if (intent.to === 'add-credits') {
            const qs = `?penmateId=${encodeURIComponent(intent.penmateId)}${
              intent.letterId ? `&letterId=${encodeURIComponent(intent.letterId)}` : ''
            }&returnTo=${encodeURIComponent(intent.returnTo || returnTo)}`;
            this.navCtrl.navigateForward(`/billing/add-credits${qs}`);
            return;
          }
          if (intent.to === 'legacy-message') {
            const emessage = intent.emessage ? 'true' : 'false';
            this.navCtrl.navigateForward(
              `/penmates/${encodeURIComponent(intent.penmateId)}/messages/${encodeURIComponent(
                intent.messageId,
              )}?emessage=${emessage}`,
            );
            return;
          }
          if (intent.to === 'tracking') {
            void this.openLegacyDeliveryTracking({
              penmateId: intent.penmateId,
              messageId: intent.messageId,
            });
            return;
          }
          if (intent.to === 'needs-info') {
            void this.openLegacyNeedsInfo({
              penmateId: intent.penmateId,
              messageId: intent.messageId,
            });
            return;
          }
        });
      },
    };

    this.zone.runOutsideAngular(() => {
      if (!this.isMounted) {
        api.mount(el, props);
        this.isMounted = true;
      } else {
        api.update(el, props);
      }
    });
  }

  private startRouteSync(): void {
    if (this.routeSyncSub) return;
    this.routeSyncSub = merge(this.route.paramMap, this.route.queryParamMap)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(() => {
        if (!this.isMounted) return;
        // Keep React props in sync with URL changes (e.g. resend deep-link adds `?letterId=...`).
        try {
          this.render();
        } catch (e) {
          // If navigation races script load/unmount, ignore and let the next navigation/render win.
        }
      });
  }

  private async openLegacyDeliveryTracking(args: { penmateId: string; messageId: string }): Promise<void> {
    const messageId = args && args.messageId ? String(args.messageId) : '';
    const penmateId = args && args.penmateId ? String(args.penmateId) : '';
    if (!messageId || !penmateId) return;

    const loading = await this.modalCtrl.create({
      component: LoadingModal,
      componentProps: { message: 'Loading delivery…' },
    });
    await loading.present();

    this.letterService.getLetter(messageId).subscribe(
      async (res: any) => {
        await loading.dismiss();
        const data = res && typeof res.data === 'object' ? res.data : {};
        const currentMessage = Object.assign({}, data, res);
        // Ensure the Leaflet/mapbox directive renders a map inside the delivery modal.
        // The directive depends on fields like `state` / `data.state` / `data.facility`.
        // `this.penmate` can be a minimal object (id/slug/name) depending on how the messenger was entered,
        // so prefer the fuller penmate/inmate payload from the fetched letter response.
        const letterPenmate =
          (currentMessage && (currentMessage.inmate || currentMessage.recipient)) || null;
        const penmateForModals = Object.assign(
          {},
          this.penmate || {},
          letterPenmate || {},
          { showMap: true },
        );

        const openTracking = async () => {
          const trackingModal = await this.modalCtrl.create({
            component: MessageTrackingModal,
            cssClass: 'message-tracking-popover ',
            showBackdrop: true,
            componentProps: {
              navCtrl: this.navCtrl,
              penmate: penmateForModals,
              currentMessage,
              showBackdrop: true,
            },
          });
          await trackingModal.present();
        };

        const openEditAddress = async () => {
          const modal = await this.modalCtrl.create({
            component: MessageIncompleteModal,
            cssClass: 'message-incomplete-modal',
            showBackdrop: true,
            componentProps: {
              navCtrl: this.navCtrl,
              penmate: penmateForModals,
              currentMessage,
              showBackdrop: true,
            },
          });

          modal.onDidDismiss().then(() => {
            setTimeout(() => {
              this.threadRefreshKey += 1;
              this.render();
            }, 1000);
          });

          await modal.present();
        };

        const deliveryModal = await this.modalCtrl.create({
          component: MessageDeliveryModal,
          cssClass: 'message-tracking-popover message-delivery-modal',
          showBackdrop: true,
          componentProps: {
            navCtrl: this.navCtrl,
            penmate: penmateForModals,
            currentMessage,
            onShowTracking: openTracking,
            onEditAddress: openEditAddress,
            showBackdrop: true,
          },
        });
        await deliveryModal.present();
      },
      async () => {
        await loading.dismiss();
        this.navCtrl.navigateForward(
          `/penmates/${encodeURIComponent(penmateId)}/messages/${encodeURIComponent(messageId)}?emessage=false`,
        );
      },
    );
  }

  private async openLegacyNeedsInfo(args: { penmateId: string; messageId: string }): Promise<void> {
    const messageId = args && args.messageId ? String(args.messageId) : '';
    const penmateId = args && args.penmateId ? String(args.penmateId) : '';
    if (!messageId || !penmateId) return;

    const loading = await this.modalCtrl.create({
      component: LoadingModal,
      componentProps: { message: 'Loading details…' },
    });
    await loading.present();

    this.letterService.getLetter(messageId).subscribe(
      async (res: any) => {
        await loading.dismiss();
        const data = res && typeof res.data === 'object' ? res.data : {};
        const currentMessage = Object.assign({}, data, res);

        const modal = await this.modalCtrl.create({
          component: MessageIncompleteModal,
          cssClass: 'message-incomplete-modal',
          showBackdrop: true,
          componentProps: {
            navCtrl: this.navCtrl,
            penmate: this.penmate,
            currentMessage,
            showBackdrop: true,
          },
        });

        modal.onDidDismiss().then(() => {
          setTimeout(() => {
            this.threadRefreshKey += 1;
            this.render();
          }, 1000);
        });

        await modal.present();
      },
      async () => {
        await loading.dismiss();
        this.navCtrl.navigateForward(
          `/penmates/${encodeURIComponent(penmateId)}/messages/${encodeURIComponent(messageId)}?emessage=false`,
        );
      },
    );
  }

}
